import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useOnboarding } from "./onboardingStore";
import { CHECKLIST_ITEMS, INTROS, TOURS, checklistFor } from "./tours";
import Spotlight from "./Spotlight";
import { CelebrationModal, Check, PageIntroModal, SensaMark, WelcomeModal } from "./OnboardingModals";

const isItemDone = (state, key) => (key === "tour" ? !!state?.done?.tour : !!state?.done?.[key]);

const Chevron = ({ up }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#77707E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={up ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
  </svg>
);

const Checklist = ({ items, state, collapsed, onStart, onToggle, onDismiss, bottom }) => {
  const doneCount = items.filter((k) => isItemDone(state, k)).length;
  const allDone = doneCount === items.length;
  const nextKey = items.find((k) => !isItemDone(state, k));
  const pct = `${Math.round((doneCount / items.length) * 100)}%`;

  return createPortal(
    <div
      className="fixed right-6 font-outfit w-[356px] max-w-[calc(100vw-32px)] bg-white rounded-2xl border border-[rgba(160,74,151,0.18)] shadow-[0_2px_6px_rgba(24,16,28,0.06),0_28px_60px_-24px_rgba(24,16,28,0.55)] overflow-hidden"
      style={{ bottom, zIndex: 9000 }}
    >
      <button type="button" onClick={onToggle} aria-expanded={!collapsed} className="w-full px-4 pt-4 pb-3 flex items-center gap-2.5 text-left">
        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B85BAE] via-[#A04A97] to-[#C86B4F] text-white flex items-center justify-center flex-none">
          <SensaMark size={18} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold text-[#18101C]">{allDone ? "You're all set" : "Getting started"}</span>
          <span className="block mt-px text-[12.5px] text-[#77707E] tabular-nums">
            {doneCount} of {items.length} complete
          </span>
        </span>
        <Chevron up={collapsed} />
      </button>
      <div className="mx-4 mb-3 h-1.5 rounded-full bg-[rgba(160,74,151,0.12)] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C95EBE] transition-[width] duration-500" style={{ width: pct }} />
      </div>
      {!collapsed && (
        <div className="px-2 pb-2 flex flex-col gap-0.5">
          {items.map((key) => {
            const item = CHECKLIST_ITEMS[key];
            const done = isItemDone(state, key);
            if (done) {
              return (
                <div key={key} className="flex items-center gap-2.5 px-2 py-2">
                  <span className="w-5 h-5 rounded-full bg-[#A04A97] flex items-center justify-center flex-none">
                    <Check size={11} />
                  </span>
                  <span className="text-[14px] text-[#77707E] line-through">{item.label}</span>
                </div>
              );
            }
            if (key === nextKey) {
              return (
                <div key={key} className="flex items-start gap-2.5 px-2 py-2.5 rounded-[10px] bg-[rgba(160,74,151,0.07)] shadow-[inset_0_0_0_1px_rgba(160,74,151,0.25)]">
                  <span className="mt-px w-5 h-5 rounded-full border-[1.5px] border-[#A04A97] flex-none box-border" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold text-[#18101C]">{item.label}</span>
                    <span className="block mt-0.5 text-[12.5px] leading-[1.4] text-[#5B5563]">{item.hint}</span>
                  </span>
                  <button type="button" onClick={() => onStart(key)} className="px-3 py-1 rounded-md bg-[#A04A97] hover:bg-[#8B3F83] text-white text-[13px] font-semibold">
                    Start
                  </button>
                </div>
              );
            }
            return (
              <button key={key} type="button" onClick={() => onStart(key)} className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#F9F6F9]">
                <span className="w-5 h-5 rounded-full border-[1.5px] border-[#D4D0D8] flex-none box-border" />
                <span className="text-[14px] text-[#18101C]">{item.label}</span>
              </button>
            );
          })}
          <button type="button" onClick={onDismiss} className="self-end mt-1 mr-1 px-2 py-1 text-[12.5px] text-[#77707E] hover:text-[#18101C] underline">
            {allDone ? "Close" : "Hide checklist"}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

const ToursMenu = ({ state, onPick, onIntro, onToggleChecklist, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const rows = [
    ...Object.entries(TOURS).map(([id, t]) => ({ key: id, label: t.label, done: !!state?.seen?.[t.seenKey], onClick: () => onPick(id) })),
    ...Object.entries(INTROS)
      .filter(([, intro]) => !intro.tour)
      .map(([key, intro]) => ({ key, label: `${intro.name} intro`, done: !!state?.seen?.[key], onClick: () => onIntro(key) })),
  ];
  const checklistOn = !state?.checklist?.hidden;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Product tours"
      className="fixed left-[248px] bottom-2 w-[300px] max-w-[calc(100vw-32px)] font-outfit bg-white rounded-[10px] border-[0.5px] border-[#B3B3B3] shadow-[0_12px_30px_-10px_rgba(0,0,0,0.25)] px-2 pt-2.5 pb-2"
      style={{ zIndex: 100000 }}
    >
      <p className="px-2.5 pb-2 text-[12px] font-semibold tracking-[0.08em] uppercase text-[#77707E]">Replay a tour</p>
      {rows.map((r) => (
        <button key={r.key} type="button" role="menuitem" onClick={r.onClick} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left text-[14px] text-[#18101C] hover:bg-[#F5EDF4]">
          <span className="flex-1">{r.label}</span>
          <span className={`text-[12px] font-medium ${r.done ? "text-[#15803D]" : "text-[#A04A97]"}`}>{r.done ? "Done" : "Not yet"}</span>
        </button>
      ))}
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={checklistOn}
        onClick={onToggleChecklist}
        className="mt-1.5 mx-1 pt-2.5 pb-0.5 px-1.5 w-[calc(100%-8px)] border-t border-[#F3F4F6] flex items-center justify-between text-[13.5px] text-[#3F3F46]"
      >
        Show getting-started checklist
        <span className={`relative w-8 h-[18px] rounded-full transition-colors ${checklistOn ? "bg-[#A04A97]" : "bg-[#D4D0D8]"}`}>
          <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-[left] ${checklistOn ? "left-4" : "left-0.5"}`} />
        </span>
      </button>
    </div>,
    document.body
  );
};

const Toast = ({ message, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 4500);
    return () => clearTimeout(t);
  }, [message, onDone]);
  return createPortal(
    <div
      role="status"
      className="fixed left-1/2 top-6 -translate-x-1/2 flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full bg-[#18101C] text-white text-[14px] font-outfit shadow-[0_12px_30px_-10px_rgba(24,16,28,0.6)]"
      style={{ zIndex: 10070 }}
    >
      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#A04A97] to-[#C95EBE] flex items-center justify-center flex-none">
        <Check />
      </span>
      {message}
    </div>,
    document.body
  );
};

const OnboardingHost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loaded,
    state,
    profile,
    activeTour,
    toursMenuOpen,
    forcedIntro,
    celebration,
    toast,
    sessionSkipped,
    load,
    markSeen,
    setChecklist,
    startTour,
    goToStep,
    endTour,
    skipForSession,
    setToursMenuOpen,
    showIntro,
    clearForcedIntro,
    closeCelebration,
    clearToast,
  } = useOnboarding();

  // On Sensa and on small screens the open card covers the page, so it starts folded there.
  const [openWhenCramped, setOpenWhenCramped] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  const tour = activeTour ? TOURS[activeTour.id] : null;
  const { pathname, search } = location;

  // A tour belongs to one page: go there first, and open the nav so its labels can be spotlit.
  useEffect(() => {
    if (!tour) return;
    if (tour.expandNav) window.dispatchEvent(new Event("onboarding-expand-nav"));
    if (pathname !== tour.route) navigate(tour.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTour?.id]);

  const items = useMemo(() => checklistFor(profile), [profile]);
  const firstName = (profile.fullName || "").split(" ")[0];

  const startItem = useCallback(
    (key) => {
      const item = CHECKLIST_ITEMS[key];
      if (item.tour) startTour(item.tour);
      else navigate(item.route);
    },
    [navigate, startTour]
  );

  const handleNext = useCallback(() => {
    if (!activeTour) return;
    if (activeTour.step >= tour.steps.length - 1) endTour(true);
    else goToStep(activeTour.step + 1);
  }, [activeTour, tour, endTour, goToStep]);

  const handleMissing = useCallback(() => {
    if (!activeTour) return;
    if (activeTour.step >= tour.steps.length - 1) endTour(false);
    else goToStep(activeTour.step + 1);
  }, [activeTour, tour, endTour, goToStep]);

  const closeToursMenu = useCallback(() => setToursMenuOpen(false), [setToursMenuOpen]);

  if (!loaded || !state) return null;

  const welcomeOpen = !state.seen?.welcome;
  const onSensa = pathname.startsWith("/rfp-copilot");
  const cramped = onSensa || window.innerWidth < 640;

  const introKey =
    forcedIntro ||
    Object.keys(INTROS).find((key) => {
      const intro = INTROS[key];
      return intro.matches(pathname, search) && !state.seen?.[key] && !sessionSkipped.includes(key);
    });
  const forcedMismatch = forcedIntro && !INTROS[forcedIntro].matches(pathname, search);
  const introOpen = !!introKey && !forcedMismatch && !welcomeOpen && !activeTour && !celebration;

  const doneCount = items.filter((k) => isItemDone(state, k)).length;
  const nextKey = items.find((k) => !isItemDone(state, k));
  const checklistOpen =
    !welcomeOpen && !introOpen && !activeTour && !celebration && !state.checklist?.hidden && !pathname.startsWith("/account");

  return (
    <>
      {welcomeOpen && (
        <WelcomeModal
          firstName={firstName}
          onStart={() => {
            markSeen("welcome");
            skipForSession("intro_dashboard");
            startTour("app");
          }}
          onSkip={() => {
            markSeen("welcome");
            skipForSession("intro_dashboard");
          }}
        />
      )}

      {introOpen && (
        <PageIntroModal
          key={introKey}
          intro={INTROS[introKey]}
          onPrimary={() => {
            const intro = INTROS[introKey];
            markSeen(introKey);
            clearForcedIntro();
            if (intro.tour) startTour(intro.tour);
          }}
          onLater={() => {
            skipForSession(introKey);
            clearForcedIntro();
          }}
          onNever={() => {
            markSeen(introKey);
            clearForcedIntro();
          }}
        />
      )}

      {tour && (
        <Spotlight
          key={`${activeTour.id}-${activeTour.step}`}
          step={tour.steps[activeTour.step]}
          index={activeTour.step}
          total={tour.steps.length}
          onNext={handleNext}
          onBack={() => goToStep(Math.max(0, activeTour.step - 1))}
          onSkip={() => endTour(false)}
          onMissing={handleMissing}
        />
      )}

      {celebration && (
        <CelebrationModal
          projectName={celebration.projectName}
          doneCount={doneCount}
          total={items.length}
          nextLabel={nextKey ? CHECKLIST_ITEMS[nextKey].label : null}
          onClose={closeCelebration}
        />
      )}

      {checklistOpen && (
        <Checklist
          items={items}
          state={state}
          bottom={onSensa ? 112 : 24}
          collapsed={cramped ? !openWhenCramped : !!state.checklist?.collapsed}
          onStart={startItem}
          onToggle={() => (cramped ? setOpenWhenCramped((o) => !o) : setChecklist({ collapsed: !state.checklist?.collapsed }))}
          onDismiss={() => setChecklist({ hidden: true })}
        />
      )}

      {toursMenuOpen && (
        <ToursMenu
          state={state}
          onPick={startTour}
          onIntro={(key) => {
            showIntro(key);
            const route = INTROS[key].route;
            if (!INTROS[key].matches(pathname, search)) navigate(route);
          }}
          onToggleChecklist={() => setChecklist({ hidden: !state.checklist?.hidden })}
          onClose={closeToursMenu}
        />
      )}

      {toast && <Toast message={toast} onDone={clearToast} />}
    </>
  );
};

export default OnboardingHost;
